import { useShowSessionsListAssistantView } from '@/components/layout/Navigation/sessions/hooks/useShowSessionsListAssistantView';
import { StackedSourceAvatars } from '@/components/ui/floating-ask-heidi/evidence-prompts-bar';
import { useTrialRecapUsageStats } from '@/features/trialsAndUpgrades/hooks/useTrialRecapUsageStats';
import { useQuerySourceRecommendations } from '@/hooks/query/useQuerySourceRecommendations';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { cn } from '@/lib/cn';
import { useUserTier } from '@heidi/commerce/data';
import { Badge } from '@heidi/design-system/badge';
import { Button } from '@heidi/design-system/button';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyP } from '@heidi/design-system/typography';
import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  formatEvidenceUsageBadge,
  getTrialRecapFeatureMeta,
  mapEvidenceUsageToTrialRecapFeatures,
  TRIAL_RECAP_FEATURE_USAGE_BADGE_FORMAT,
  type TrialRecapFeatureKey,
  type TrialRecapFeatureLabelTooltip,
  type TrialRecapFeatureMeta,
} from './utils';

interface TrialRecapFeatureListProps {
  /**
   * When set, replaces the default Evidence answers row tooltip (e.g. trial
   * recap copy vs. trial-ended upgrade messaging).
   */
  evidenceAnswersLabelTooltip?: TrialRecapFeatureLabelTooltip;
}

/**
 * Self-contained list of feature-usage rows for the trial recap experience.
 *
 * Each row renders an icon, label, and (when the user has engaged with the
 * feature) a usage badge on the right — "Used Nx" for Evidence answers,
 * "N sessions" for Answers in patient sessions, and a stacked-favicon row
 * plus reference count for Citations sourced. **Rows with zero usage hide
 * the right-hand widget entirely**; the row stays so the recap visually
 * acknowledges every Evidence feature, but no Try now CTA is offered (the
 * design moved away from in-row deep-links — the primary upgrade CTA is the
 * only call to action in this modal).
 *
 * The Answers-in-patient-sessions row is hidden when the user has no
 * in-session evidence access (`useEvidenceAccess` returns false for UK / EU
 * EEA, and while the access lookup is pending) AND no prior usage. With
 * prior usage the row remains so the recap still reflects what they used
 * during the trial.
 */
export const TrialRecapFeatureList = ({
  evidenceAnswersLabelTooltip,
}: TrialRecapFeatureListProps) => {
  const intl = useIntl();
  const { userTier } = useUserTier();
  const isClinicianPlan =
    userTier === SubscriptionTier.CLINICIAN_PRO || userTier === SubscriptionTier.PRO;
  const { usageStats } = useTrialRecapUsageStats(true);

  const showSessionsListAssistantView = useShowSessionsListAssistantView();
  const { isInSessionEvidenceShown } = useEvidenceAccess();

  const features = useMemo(() => {
    const all = mapEvidenceUsageToTrialRecapFeatures(usageStats);
    if (showSessionsListAssistantView || !isInSessionEvidenceShown) {
      return all.filter(
        ({ key, count }) => key !== 'answersInPatientSessions' || count > 0
      );
    }
    return all;
  }, [usageStats, showSessionsListAssistantView, isInSessionEvidenceShown]);

  const featureMeta = useMemo((): Record<TrialRecapFeatureKey, TrialRecapFeatureMeta> => {
    const base = getTrialRecapFeatureMeta();
    if (!evidenceAnswersLabelTooltip) {
      return base;
    }
    return {
      ...base,
      evidenceAnswers: {
        ...base.evidenceAnswers,
        labelTooltip: evidenceAnswersLabelTooltip,
      },
    };
  }, [evidenceAnswersLabelTooltip]);

  const { data: recommendationsData } = useQuerySourceRecommendations();
  const sourceCollectionAvatars = useMemo(
    () =>
      (recommendationsData?.sources ?? []).slice(0, 3).map((s) => ({ domain: s.domain })),
    [recommendationsData?.sources]
  );

  return (
    <ul className="flex w-full flex-col">
      {features.map(({ key, count }) => {
        const { Icon, label, labelTooltip } = featureMeta[key];
        return (
          <li
            key={key}
            className="flex items-center gap-2 border-b border-border px-2 py-4 last:border-b-0"
          >
            <Icon aria-hidden className="size-5 shrink-0 text-text-primary" />
            <div className="flex min-w-0 flex-1 items-center gap-1">
              <TypographyP className="min-w-0 items-center text-text-primary">
                <FormattedMessage {...label} />
              </TypographyP>
              {labelTooltip && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      iconOnly
                      className="size-6 shrink-0 text-text-secondary hover:text-text-primary"
                      aria-label={intl.formatMessage(labelTooltip.triggerAriaLabel)}
                    >
                      <Info aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-start">
                    <FormattedMessage {...labelTooltip.message} />
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {(() => {
              // Source-recommendation avatars on the Citations sourced row are
              // a static preview of the user's personalised sources — they
              // show regardless of usage so the row always conveys "here's
              // where Evidence is pulling from". The numeric badge stays
              // count-gated (no "Used 0x" pill).
              const showSources =
                key === 'citationsSourced' && sourceCollectionAvatars.length > 0;
              const showBadge = count > 0;
              if (!showSources && !showBadge) return null;
              return (
                <div className="flex items-center gap-2">
                  {showSources && (
                    <StackedSourceAvatars
                      sources={sourceCollectionAvatars}
                      size="md"
                      maxVisible={3}
                    />
                  )}
                  {showBadge && (
                    <Badge
                      color={isClinicianPlan ? 'proTier' : 'neutral'}
                      size="sm"
                      className={cn(
                        'shrink-0 shadow-none',
                        !isClinicianPlan &&
                          'bg-tier-practice-muted text-tier-practice-text'
                      )}
                    >
                      {formatEvidenceUsageBadge(
                        intl,
                        TRIAL_RECAP_FEATURE_USAGE_BADGE_FORMAT[key],
                        count
                      )}
                    </Badge>
                  )}
                </div>
              );
            })()}
          </li>
        );
      })}
    </ul>
  );
};
